import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import prepareTranslate from '@/helpers/dictionary';
import { useDisclosure } from '@mantine/hooks';
import {
  Button, Center, Divider, Flex, Loader, Text,
} from '@mantine/core';
import Comments from '@/components/Comments';
import routes from '@/routes';
import {
  IconCircleFilled,
  IconEdit,
  IconEye,
  IconPlus,
} from '@tabler/icons-react';
import { routeWithParams } from '@/helpers/route';
import AgentAgreements from '@/components/ViewAgentAgreements';
import PageTemplate from '@/components/PageTemplate';
import agentMutations from '@/api/mutitations/agent';
import { formatDateToString, AGENT_TYPE } from '@/helpers/string';
import ViewAgentDetails from '@/components/ViewAgentDetails';
import ViewAgentClients from '@/components/ViewAgentClients';
import ViewAgentAlternativeFunds from '@/components/ViewAgentAlternativeFunds';
import AgentCommisions from '@/components/AgentCommisions';
import AgentExpensesCreate from '@/components/AgentExpensesCreate';
import ViewAgentFiles from '@/components/viewAgentFiles';
import ViewAgentExpenses from '@/components/ViewAgentExpenses';
import ViewAgentEmails from '@/components/ViewAgentEmails';
import agentEditValidations from '@/helpers/validation/pages/agentEdit';
import ViewAgentAdditionalFees from '@/components/ViewAgentAdditionalFees';
import AgentAdditionalFeeCreate from '@/components/AgentAdditionalFeeCreate';
import EditAgentDetails from '@/components/EditAgentDetails';
import { useForm } from '@mantine/form';
import validations from '@/helpers/validation';
import agentApi from '@/api/services/agent';
import Modal from '@/components/Modal/Index';
import styles from './style.module.css';

const Agentview = () => {
  const {
    state, id, tab, status: agentStatus,
  } = useParams();
  const isEdit = state === 'edit';

  const [agent, setAgent] = useState(null);
  const [createExpenseOpened,
    { open: openCreateExpense, close: closeCreateExpense }] = useDisclosure(false);
  const [createFeeOpened, { open: openCreateFee, close: closeCreateFee }] = useDisclosure(false);

  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose }] = useDisclosure();

  // eslint-disable-next-line no-unused-vars
  const [loading, { open, close }] = useDisclosure(false);

  const isActive = agent?.status !== 'archive';

  const t = prepareTranslate();
  const navigate = useNavigate();
  const { useGetOne } = agentMutations;
  const getOne = useGetOne();
  const statusTitle = isActive
    ? t('global.status.active')
    : t('global.status.inactive');
  const [refreshKey, setRefreshKey] = useState(0);
  const [resultLabel, setResultLabel] = useState('');
  const [resultActions, setResultActions] = useState([]);
  const { useUpdateAgent } = agentMutations;
  const { mutateAsync: updateAgentMutate, isPending: isUpdatingAgent } = useUpdateAgent();
  const form = useForm({
    initialValues: {
      personalDetails: {
        children: [],
        taxDeductionsDateFiles: [],
        policyEndDateFiles: [],
        agentLicenseDateFile: [],
      },
      agreements: [{ branchId: [] }],
      clients: [{ client: '' }],
      expenses: [{ type: '' }],
    },
    validate: {
      status: (value) => validations.required(value),
      agentType: (value) => validations.required(value),
      employeeType: (value) => validations.required(value),
      deletionReason: (value) => validations.required(value),
      personalDetails: {
        firstName: (value) => validations.required(value),
        lastName: (value) => validations.required(value),
        uid: (value) => validations.required(value) || validations.uid(value),
        gender: (value) => validations.required(value) || validations.required(value),
        dob: (value) => validations.required(value),
        email: (value) => value && validations.email(value),
        phone: (value) => value && validations.phone(value),
        website: (value) => value && validations.required(value),
        spousePhone: (value) => value && validations.phone(value),
        spouseEmail: (value) => value && validations.email(value),
        extraPhone: (value) => value && validations.phone(value),
        homePhone: (value) => value && validations.phone(value),
        children: {
          childrenName: (value) => validations.required(value),
        },
      },
      agreements: {
        agentNumber: (value) => validations.required(value),
        from: (value) => validations.required(value),
        companyId: (value) => validations.required(value),
        branchId: (value) => validations.required(value),
        accordType: (value) => validations.required(value),
      },
      clients: {
        companyId: (_, values, p) => agentEditValidations.companyOrHp(values, p),
        hp: (_, values, p) => agentEditValidations.companyOrHp(values, p),
        accordType: (value) => validations.required(value),
        clientNumber: (value) => validations.required(value),
        branchId: (value) => validations.required(value),
      },
      expenses: {
        value: (value) => validations.required(value),
        type: (value) => validations.required(value),
        date: (value) => validations.required(value),
      },
    },
  });

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        const result = await getOne.mutateAsync({ id, status: agentStatus });
        if (!result) return;

        setAgent(result);
        const {
          personalDetails, status, agentType, employeeType,
        } = result;

        const {
          taxDeductionsDate,
          policyEndDate,
          agentLicenseDate,
          taxDeductionsDateFiles,
          policyEndDateFiles,
          agentLicenseDateFile,
        } = personalDetails || {};

        form.setValues({
          id,
          personalDetails: {
            ...personalDetails,
            taxDeductionsDateFiles: taxDeductionsDateFiles || [],
            policyEndDateFiles: policyEndDateFiles || [],
            agentLicenseDateFile: agentLicenseDateFile || [],
            taxDeductionsDate: taxDeductionsDate ? new Date(taxDeductionsDate) : null,
            policyEndDate: policyEndDate ? new Date(policyEndDate) : null,
            agentLicenseDate: agentLicenseDate ? new Date(agentLicenseDate) : null,
          },
          status,
          agentType,
          employeeType,
        });
      } catch (error) {
        navigate(routes.agents.path);
      }
    };

    fetchAgent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refreshKey]);

  if (!agent) {
    return <Center><Loader /></Center>;
  }

  // TODO: move to agentview functions
  const menuGroups = [
    {
      links: [
        {
          id: 'details',
          title: t('pages.agentview.menu.details'),
        },
        {
          id: 'contracts',
          title: t('pages.agentview.menu.contracts'),
        },
        {
          id: 'documents',
          title: t('pages.agentview.menu.documents'),
        },
        {
          id: 'clients',
          title: t('pages.agentview.menu.clients'),
        },
        {
          id: 'alternativeFunds',
          title: t('pages.agentview.menu.alternativeFunds'),
        },
        {
          id: 'expenses',
          title: t('pages.agentview.menu.expenses'),
        },
        {
          id: 'additionalFees',
          title: t('pages.agentview.menu.additionalfees'),
        },
        {
          id: 'commisions',
          title: t('pages.agentview.menu.commisions'),
        },
        {
          id: 'emails',
          title: t('pages.agentview.menu.emails'),
        },
      ],
    },
  ];

  // TODO: move to agentview functions
  const tabs = [
    {
      title: t('pages.agentview.side.comments'),
      content: <Comments type='agent' id={id} showForm />,
      isDefault: true,
    },
  ];

  const handleUpdateClick = () => {
    navigate(routeWithParams(routes.agentView.path, { id, tab: 'details', state: 'edit' }));
  };

  const information = <Flex align="center" m={12} mr={0}>
  <IconCircleFilled color="#01C929" size={10} />
  <Text c='#6B6E77' size='xs' mr={10}>{statusTitle}</Text>
  {agent?.agentType && (
  <Divider
    orientation="vertical"
    size="xs"
    className={styles.agentDivider}
  />
  )}
  {agent?.agentType && (
    <IconCircleFilled color="#FF9583" size={10} />
  )}
  {agent?.agentType && (
    <Text c='#6B6E77' size='xs' mr={10}>{AGENT_TYPE[agent?.agentType]}</Text>)}
  </Flex>;

  const onArchivedExpensesClick = () => {
    navigate(routeWithParams(routes.archiveExpenses.path, { id }));
  };

  const onArchivedFeesClick = () => {
    navigate(routeWithParams(routes.archiveFees.path, { id }));
  };

  const onArchivedEmailsClick = () => {
    navigate(routeWithParams(routes.archiveEmails.path, { id }));
  };

  const headerActions = {
    details: !isEdit ? [
      <Button
        key='update'
        onClick={handleUpdateClick}
        leftSection={<IconEdit />}>
      {t('pages.agentview.action')}
      </Button>,
    ] : [],
    expenses:
      [<Button
        leftSection={<IconEye />}
        key='archive'
        variant='outline'
        onClick={onArchivedExpensesClick}
      >
        {t('components.viewAgentExpenses.archive')}
      </Button>,
        <Button
          key='create'
          leftSection={<IconPlus />}
          onClick={openCreateExpense}
       >
         {t('components.viewAgentExpenses.addExpense')}
       </Button>,
      ],
    additionalFees:
      [<Button
        leftSection={<IconEye />}
        key='archive'
        variant='outline'
        onClick={onArchivedFeesClick}
      >
        {t('components.viewAgentAdditionalFees.archive')}
      </Button>,
        <Button
          key='create'
          leftSection={<IconPlus />}
          onClick={openCreateFee}
        >
         {t('components.viewAgentAdditionalFees.addFee')}
       </Button>,
      ],
    emails:
      [<Button
        leftSection={<IconEye />}
        key='archive'
        variant='outline'
        onClick={onArchivedEmailsClick}
      >
        {t('components.viewAgentEmails.archive.title')}
      </Button>,
      ],

  };

  const titles = {
    details: isEdit ? t('pages.agentview.titleEdit', [
      agent?.personalDetails?.fullName || '',
    ]) : t('pages.agentview.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    contracts: t('components.viewAgentAgreements.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    documents: t('components.viewAgentFiles.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    clients: t('components.viewAgentClients.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    alternativeFunds: t('components.viewAgentAlternativeFunds.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    expenses: t('components.viewAgentExpenses.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    additionalFees: t('components.viewAgentAdditionalFees.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    commisions: t('components.viewAgentCommissions.title', [
      agent?.personalDetails?.fullName || '',
    ]),
    emails: t('components.viewAgentEmails.title', [
      agent?.personalDetails?.fullName || '',
    ]),
  };

  const handleAgentUpdateClick = async (valuesToUpdate) => {
    try {
      await updateAgentMutate({ id, ...valuesToUpdate });
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const onError = () => {
    setResultLabel(t('pages.agents.notification.errors.delete.title'));
    setResultActions(faildResultActions);
    actionResultOpen();
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleModalClose = () => {
    actionResultClose();
    navigate(routes.agents.path);
  };

  const sucessResultActions = [
    <Button key='closeButton' onClick={handleModalClose}>{t('global.close')}</Button>,
  ];

  const faildResultActions = [
    <Button key='closeButton' onClick={() => actionResultClose()}>{t('global.close')}</Button>,
  ];

  const handleDeleteAgent = async () => {
    const { hasError } = form.validateField('deletionReason');

    if (hasError) {
      return { sucess: false };
    }

    try {
      await agentApi.delete({ id: form.values.id, deletionReason: form.values.deletionReason });
      setResultLabel(t('pages.agentEdit.succesdDelete'));
      setResultActions(sucessResultActions);
      actionResultOpen();
      return { sucess: true };
    } catch (error) {
      setResultLabel(t('pages.agents.notification.errors.delete.title'));
      setResultActions(faildResultActions);
      actionResultOpen();
      return { sucess: false };
    }
  };

  return (
    <>
      <PageTemplate
        menuGroups={menuGroups}
        tabs={tabs}
        withBackButton = {true}
        tabsTitle={t('pages.agentview.side.tabs')}
        title= {titles[tab]}
        subtitle={t('pages.agentview.subtitle', [formatDateToString(agent?.updatedAt)])}
        information={information}
        headerActions={headerActions[tab]}
      >

        {
          isEdit ? <EditAgentDetails
          form={form}
          handleAgentUpdateOrSaveClick={handleAgentUpdateClick}
          handleDeleteAgent={handleDeleteAgent}
          handleBackClick = {handleBackClick}
          loading={isUpdatingAgent}
          onError={onError}
          setRefreshKey={setRefreshKey}
          /> : <ViewAgentDetails agent={agent} />
        }

        <PageTemplate.Panel id='contracts'>
          <AgentAgreements
            data={agent.agreements}
            id={id}
            setKey={setRefreshKey}
            agent={agent}
            key={refreshKey} />
        </PageTemplate.Panel>

        <PageTemplate.Panel id='documents'>
        <ViewAgentFiles
          open={open}
          close={close}
          agent={agent}
          id={id}
          handleAgentUpdateClick={handleAgentUpdateClick}
          setRefreshkey={setRefreshKey}
          refreshkey={refreshKey}
        />
        </PageTemplate.Panel>

        <PageTemplate.Panel id='clients'>
        <ViewAgentClients agent={agent} id={id} setKey={setRefreshKey}/>
        </PageTemplate.Panel>

        <PageTemplate.Panel id='alternativeFunds'>
          <ViewAgentAlternativeFunds agent={agent} id={id} setKey={setRefreshKey} />
        </PageTemplate.Panel>

        <PageTemplate.Panel id='expenses'>
          <ViewAgentExpenses
            agent={agent}
            setKey={setRefreshKey}
            id={id}
            key={refreshKey}
            handleAgentUpdateClick={handleAgentUpdateClick} />
          <AgentExpensesCreate
            open={openCreateExpense}
            close={closeCreateExpense}
            opened={createExpenseOpened}
            id={id}
            setKey={setRefreshKey}
          />

        </PageTemplate.Panel>

        <PageTemplate.Panel id='additionalFees'>
          <ViewAgentAdditionalFees
            agent={agent}
            setKey={setRefreshKey}
            id={id}
            key={refreshKey}
            handleAgentUpdateClick={handleAgentUpdateClick} />
          <AgentAdditionalFeeCreate
            open={openCreateFee}
            close={closeCreateFee}
            opened={createFeeOpened}
            id={id}
            setKey={setRefreshKey}
          />

        </PageTemplate.Panel>

        <PageTemplate.Panel id='commisions'>
          <AgentCommisions
            agent={agent}
            id={id}
          />
        </PageTemplate.Panel>

        <PageTemplate.Panel id='emails'>
        <ViewAgentEmails agent={agent} id={id} setKey={setRefreshKey} />
        </PageTemplate.Panel>

      </PageTemplate>
      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />

    </>
  );
};

export default Agentview;
